import { Link, Redirect, useLocation, useParams } from "react-router-dom";
import {
  BlogBlock,
  formatBlogDate,
  getPostForLang,
} from "../../content/blogPosts";
import Seo from "../Seo";
import NewsletterForm from "../newsletter/NewsletterForm";
import BlogCta from "./BlogCta";
import RelatedPosts from "./RelatedPosts";
import styles from "./blog.module.css";

const BASE_URL = "https://shifttracker.net";
const OG_IMAGE_URL = `${BASE_URL}/og-image.jpg`;
const LOGO_URL = `${BASE_URL}/logo.png`;

const UI = {
  tr: { back: "← Tüm yazılar" },
  en: { back: "← All articles" },
};

function renderBlock(block: BlogBlock, index: number): JSX.Element | null {
  switch (block.type) {
    case "heading":
      return (
        <h2 key={index} className={styles.articleHeading}>
          {block.text}
        </h2>
      );
    case "paragraph":
      if (block.segments) {
        return (
          <p key={index} className={styles.articleParagraph}>
            {block.segments.map((segment, i) =>
              segment.to ? (
                <Link key={i} to={segment.to} className={styles.articleLink}>
                  {segment.text}
                </Link>
              ) : (
                <span key={i}>{segment.text}</span>
              )
            )}
          </p>
        );
      }
      return (
        <p key={index} className={styles.articleParagraph}>
          {block.text}
        </p>
      );
    case "list":
      return (
        <ul key={index} className={styles.articleList}>
          {(block.items ?? []).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}

function BlogPost(): JSX.Element {
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const isEnglish = location.pathname.startsWith("/en/");
  const lang: "tr" | "en" = isEnglish ? "en" : "tr";
  const prefix = isEnglish ? "/en" : "";
  const ui = UI[lang];

  const post = getPostForLang(slug, isEnglish);
  if (!post) {
    return <Redirect to={`${prefix}/blog/`} />;
  }

  const content = post[lang];
  const path = `/blog/${post.slug}`; // Turkish path
  const enPath = `/blog/${post.enSlug}`; // English path
  const canonical = isEnglish
    ? `${BASE_URL}/en${enPath}/`
    : `${BASE_URL}${path}/`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: content.title,
    description: content.description,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: isEnglish ? "en-US" : "tr-TR",
    image: OG_IMAGE_URL,
    author: { "@type": "Organization", name: "Shift Tracker", url: `${BASE_URL}/` },
    publisher: {
      "@type": "Organization",
      name: "Shift Tracker",
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
    mainEntityOfPage: canonical,
  };

  return (
    <div className={styles.page}>
      <Seo
        path={path}
        enPath={enPath}
        title={`${content.title} - Shift Tracker`}
        description={content.description}
        type="article"
        schema={schema}
      />
      <div className={styles.container}>
        <Link className={styles.back} to={`${prefix}/blog/`}>
          {ui.back}
        </Link>
        <article className={styles.article}>
          <time className={styles.date} dateTime={post.date}>
            {formatBlogDate(post.date, lang)}
          </time>
          <h1 className={styles.articleTitle}>{content.title}</h1>
          {content.body.map((block, index) => renderBlock(block, index))}
        </article>
        <BlogCta lang={lang} source={`blog:${post.slug}`} />
        <RelatedPosts currentSlug={post.slug} lang={lang} />
        <NewsletterForm source={`blog:${post.slug}`} />
      </div>
    </div>
  );
}

export default BlogPost;
